import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { TrendingUp, PieChart, Calendar, Target, ArrowRight, BookOpen, Plane } from "lucide-react";
import axios from "axios";

import { API } from '../utils/api';

export default function Landing() {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already authenticated
    axios.get(`${API}/auth/me`)
      .then(() => navigate("/dashboard", { replace: true }))
      .catch(() => {}); // Not authenticated, stay on landing
  }, [navigate]);

  const features = [
    {
      icon: TrendingUp,
      title: "Track All Accounts",
      description: "Monitor savings, investments, and categorize by owner"
    },
    {
      icon: PieChart,
      title: "Asset Distribution",
      description: "Visualize your portfolio across equity, debt, and commodities"
    },
    {
      icon: Calendar,
      title: "Monthly & Annual",
      description: "Log income and expenses with automatic savings calculation"
    },
    {
      icon: Target,
      title: "Future Planning",
      description: "Forecast your wealth with salary growth and interest rates"
    },
    {
      icon: Plane,
      title: "Holiday Budgeting",
      description: "Plan and track your vacation expenses vs actual spending"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Clean with only logo and name */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-black" />
            </div>
            <span className="font-bold text-xl text-white tracking-tight">LifeLedger</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div 
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold text-green-400 uppercase tracking-widest mb-4">
              Financial Planning Made Simple
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight mb-6">
              Take Control of Your <span className="gradient-text">Financial Future</span>
            </h1>
            <p className="text-lg text-gray-400 leading-relaxed mb-8 max-w-xl">
              Track accounts, monitor investments, plan for the future. All in one beautiful, intuitive dashboard designed for clarity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate("/register")}
                data-testid="hero-signup-btn"
                size="lg"
                className="bg-green-500 hover:bg-green-400 text-black rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-green-500/20 group"
              >
                Sign Up
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => navigate("/login")}
                data-testid="hero-login-btn"
                size="lg"
                variant="outline"
                className="border-gray-600 text-white hover:bg-gray-800 rounded-full px-8 py-6 text-lg font-semibold"
              >
                Login
              </Button>
            </div>
          </motion.div>

          <motion.div 
            className="lg:col-span-5"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-green-500/10 to-cyan-500/10 rounded-2xl blur-3xl"></div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/83df1f87-d735-4d06-9fb9-d2c80d5628f3/images/a8bdcdf80d162ff73a6609f52692aca31b564a387377e2f9e931cd1f65901f8e.png"
                alt="LifeLedger financial planning dashboard on laptop showing total balance, asset allocation pie chart, and retirement planning"
                className="relative rounded-2xl shadow-2xl"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 md:px-8 lg:px-12 bg-card/50">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold text-green-400 uppercase tracking-widest mb-4">Features</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Everything You Need
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card border border-border rounded-xl p-6 card-hover"
              >
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 md:px-8 lg:px-12 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-black" />
            </div>
            <span className="font-semibold text-white">LifeLedger</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-right">
            <a href="/admin" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
              Admin
            </a>
            <p className="text-sm text-gray-500">© 2025 LifeLedger. All rights reserved.</p>
            <p className="text-sm text-gray-500">
              Created by{" "}
              <a 
                href="https://www.linkedin.com/in/amitdhawan/?originalSubdomain=nl" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-green-400 hover:text-green-300 transition-colors"
              >
                Amit Dhawan
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
